/**
 * Compare — §18, side by side, on a phone.
 *
 * ── The table is transposed, and that is the whole design ───────────────────
 *
 * §18 lists eleven fields to compare across the finalists. Laid out the
 * obvious way — one row per opportunity, one column per field — eleven columns
 * do not fit on a 390px screen and never will, so the reader ends up scrolling
 * horizontally to find a value and vertically to find the row it belongs to,
 * losing one axis every time they move along the other.
 *
 * Transposed, the FIELD is the row and the OPPORTUNITY is the column. The
 * label column is 104px and sticky, so whatever you have scrolled to, you can
 * still see what you are looking at; the opportunity columns are 132px and
 * scroll-snapped, so a swipe lands on a column rather than between two. Two
 * columns and a bit of the third are visible at 390px, which is the comparison
 * people actually make.
 *
 * ── The best cell in a row is tinted, but only where "best" means something ──
 *
 * A lower asking price is better. A lower landed cost is better. A higher
 * score is better. A LOCATION is not better, and neither is a size — a 32-foot
 * trailer is not a superior 24-foot one, it is a different decision. So seven
 * rows tint their winner and four deliberately do not, because a tint on a row
 * with no ordering invents a ranking the data does not support, and the owner
 * would read it as advice.
 *
 * Ties tint every cell that holds the winning value, and a row where every
 * column is equal tints nothing: highlighting all four says "these differ" when
 * they do not.
 *
 * ── Four columns, and the reason for the cap ────────────────────────────────
 *
 * §10.3 caps it at four. Each column costs one detail request plus, in a
 * second wave, its cost, transport and seller reads — sixteen small requests
 * at the very worst, on an explicit action, never on the feed. A fifth column
 * would be both unreadable and a request budget nobody agreed to.
 *
 * ── Selection lives in the URL ──────────────────────────────────────────────
 *
 * `/compare?ids=a,b,c`. So a comparison can be sent to somebody, survives a
 * reload, and the back button walks out of it the way it walked in.
 *
 * ── §18, §10.3 ──────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { get } from '../lib/api'
import { useLocale, type Locale } from '../lib/i18n'
import { Link, navigate, useRoute } from '../lib/route'
import { DASH, TONES, money, range, score as scoreText, scoreTone, feet, accessLabel, accessTone } from '../lib/format'
import {
  CONDITION_GRADES,
  RISK_LEVELS,
  conditionLabel,
  humanise,
  planStatusLabel,
  riskLevelLabel,
  stageLabel,
  utilityLabel,
} from '../lib/domain'
import Icon from '../components/Icon'
import { MiniCard, type FeedCard, type FeedPage } from '../components/OpportunityCard'
import type {
  CostSummary,
  OpportunityPayload,
  SellerPayload,
  TransportPayload,
} from '../components/detail/types'

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'Compare',
  pick: 'Pick up to four',
  pickWhy: 'Finalists first, because that is the set this screen exists for. Anything you have marked shows here.',
  noFinalists: 'No finalists yet. Mark one on its own screen and it appears here.',
  max: 'Four at a time. Remove one first.',
  clear: 'Start over',
  loading: 'Loading…',
  failed: 'That one could not be loaded.',
  remove: 'Remove from the comparison',
  open: 'Open',
  emptySelection: 'Choose two to put them side by side.',

  r_score: 'Score',
  r_asking: 'Asking',
  r_landed: 'Landed',
  r_condition: 'Condition',
  r_location: 'Location',
  r_size: 'Size',
  r_utilities: 'Utilities',
  r_transport: 'Transport',
  r_repairs: 'Repairs',
  r_risks: 'Risks',
  r_seller: 'Seller',
  r_stage: 'Stage',

  none: 'None',
  unknown: 'Unknown',
  noQuotes: 'No quote yet',
  flags: 'flags',
  awaiting: 'Awaiting reply',
  silentDays: '{n} days quiet',
  noSeller: 'No seller recorded',
  best: 'Best in this row',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Comparar',
  pick: 'Escoge hasta cuatro',
  pickWhy: 'Primero los finalistas, porque para eso existe esta pantalla. Aparece aquí lo que hayas marcado.',
  noFinalists: 'Todavía no hay finalistas. Marca uno en su pantalla y aparece aquí.',
  max: 'Cuatro a la vez. Quita uno primero.',
  clear: 'Empezar de nuevo',
  loading: 'Cargando…',
  failed: 'Ese no se pudo cargar.',
  remove: 'Quitar de la comparación',
  open: 'Abrir',
  emptySelection: 'Escoge dos para ponerlos lado a lado.',

  r_score: 'Puntuación',
  r_asking: 'Piden',
  r_landed: 'Puesto aquí',
  r_condition: 'Condición',
  r_location: 'Ubicación',
  r_size: 'Tamaño',
  r_utilities: 'Servicios',
  r_transport: 'Transporte',
  r_repairs: 'Reparaciones',
  r_risks: 'Riesgos',
  r_seller: 'Vendedor',
  r_stage: 'Etapa',

  none: 'Ninguno',
  unknown: 'Se desconoce',
  noQuotes: 'Sin cotización',
  flags: 'señales',
  awaiting: 'Esperando respuesta',
  silentDays: '{n} días en silencio',
  noSeller: 'Sin vendedor anotado',
  best: 'Lo mejor de esta fila',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const MAX_COLUMNS = 4
const LABEL_WIDTH = 104
const COLUMN_WIDTH = 132

/* ────────────────────────────────────────────────────────────────────────────
 * One column's data
 * ────────────────────────────────────────────────────────────────────────── */

interface Column {
  id: string
  /** The summary the feed already had, when this one is a finalist. */
  card: FeedCard | null
  detail: OpportunityPayload | null
  cost: CostSummary | null
  transport: TransportPayload | null
  seller: SellerPayload | null
  failed: boolean
}

const blank = (id: string): Column => ({
  id,
  card: null,
  detail: null,
  cost: null,
  transport: null,
  seller: null,
  failed: false,
})

/** Every fetch below is tolerated on its own: one 500 must not blank a column. */
async function soft<T>(path: string, pick: (body: unknown) => T): Promise<T | null> {
  try {
    return pick(await get<unknown>(path))
  } catch {
    return null
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function Compare() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey): string => COPY[locale][key] ?? EN[key], [locale])
  const route = useRoute()

  const ids = useMemo(() => {
    const raw = route.query.get('ids') ?? ''
    const seen = new Set<string>()
    const out: string[] = []
    for (const one of raw.split(',').map((value) => value.trim()).filter(Boolean)) {
      if (seen.has(one)) continue
      seen.add(one)
      out.push(one)
      if (out.length === MAX_COLUMNS) break
    }
    return out
  }, [route.query])

  const [finalists, setFinalists] = useState<FeedCard[]>([])
  const [columns, setColumns] = useState<Column[]>([])
  const [note, setNote] = useState<string | null>(null)

  /* The finalist set: the picker, and the photographs and headline numbers for
     every column that is one — which is nearly all of them, for one request. */
  useEffect(() => {
    let live = true
    void (async () => {
      const page = await soft<FeedPage>(
        '/api/feed?finalist=1&disposition=all&sort=score&limit=25',
        (body) => (body as { feed: FeedPage }).feed,
      )
      if (live && page) setFinalists(page.cards)
    })()
    return () => {
      live = false
    }
  }, [])

  /* Wave one: the detail of each chosen column. Wave two fills the rest in. */
  useEffect(() => {
    let live = true
    if (ids.length === 0) {
      setColumns([])
      return
    }
    setColumns(ids.map(blank))

    void (async () => {
      await Promise.all(
        ids.map(async (id) => {
          const path = `/api/opportunities/${encodeURIComponent(id)}`
          const detail = await soft<OpportunityPayload>(path, (body) => body as OpportunityPayload)
          if (!live) return
          setColumns((held) =>
            held.map((column) =>
              column.id === id ? { ...column, detail, failed: detail === null } : column,
            ),
          )
          if (!detail) return

          const contactId = detail.opportunity.primary_contact_id
          const [cost, transport, seller] = await Promise.all([
            soft<CostSummary>(`${path}/cost`, (body) => (body as { cost: CostSummary }).cost),
            soft<TransportPayload>(`${path}/transport`, (body) => body as TransportPayload),
            contactId
              ? soft<SellerPayload>(
                  `/api/sellers/${encodeURIComponent(contactId)}`,
                  (body) => body as SellerPayload,
                )
              : Promise.resolve(null),
          ])
          if (!live) return
          setColumns((held) =>
            held.map((column) => (column.id === id ? { ...column, cost, transport, seller } : column)),
          )
        }),
      )
    })()

    return () => {
      live = false
    }
  }, [ids])

  /* Attach the feed summary once both sides have arrived. */
  const withCards = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        card: finalists.find((one) => one.id === column.id) ?? null,
      })),
    [columns, finalists],
  )

  const setIds = useCallback((next: string[]) => {
    navigate(next.length ? `/compare?ids=${next.join(',')}` : '/compare', { replace: true })
  }, [])

  const toggle = useCallback(
    (id: string) => {
      if (ids.includes(id)) {
        setIds(ids.filter((one) => one !== id))
        return
      }
      if (ids.length >= MAX_COLUMNS) {
        setNote(t('max'))
        window.setTimeout(() => setNote(null), 3000)
        return
      }
      setIds([...ids, id])
    },
    [ids, setIds, t],
  )

  const rows = useMemo(() => buildRows(withCards, locale, t), [withCards, locale, t])

  return (
    <div>
      <section style={{ marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 800 }}>{t('pick')}</h2>
        <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.4 }}>
          {t('pickWhy')}
        </p>

        {finalists.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>{t('noFinalists')}</p>
        ) : (
          finalists.map((card) => (
            <MiniCard
              key={card.id}
              card={card}
              locale={locale}
              selected={ids.includes(card.id)}
              onSelect={toggle}
            />
          ))
        )}

        {ids.length > 0 && (
          <button
            type="button"
            onClick={() => setIds([])}
            style={{
              minHeight: 40,
              padding: '0 14px',
              borderRadius: 999,
              border: '1px solid var(--line)',
              background: 'transparent',
              color: 'var(--muted)',
              font: 'inherit',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t('clear')}
          </button>
        )}
      </section>

      {ids.length === 0 ? (
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('emptySelection')}</p>
      ) : (
        <Grid columns={withCards} rows={rows} t={t} onRemove={(id) => toggle(id)} />
      )}

      {note && (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: 'calc(var(--tabbar-h) + 12px)',
            zIndex: 70,
            maxWidth: 520,
            margin: '0 auto',
            padding: '11px 14px',
            borderRadius: 12,
            background: 'var(--ink)',
            color: 'var(--bg)',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: 'var(--elev-2)',
          }}
        >
          {note}
        </div>
      )}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The rows
 *
 * A row is a label, one rendered cell per column, and — where the field has an
 * ordering at all — a comparable number per column plus which direction wins.
 * ────────────────────────────────────────────────────────────────────────── */

interface Row {
  key: string
  label: string
  cells: ReactNode[]
  /** `null` where the field has no ordering. Then nothing is tinted. */
  compare: (number | null)[] | null
  direction: 'min' | 'max'
}

type ColumnData = Column & { card: FeedCard | null }

const truthy = (value: unknown): boolean =>
  value === true || value === 1 || value === '1' || value === 'true' || value === 'yes'

const known = (value: unknown): boolean => value !== null && value !== undefined && value !== ''

function buildRows(columns: ColumnData[], locale: Locale, t: (key: CopyKey) => string): Row[] {
  const rows: Row[] = []

  const cellOf = <T,>(pick: (column: ColumnData) => T): T[] => columns.map(pick)

  /* ── Score ─────────────────────────────────────────────────────────────── */
  const scores = cellOf((column) => column.detail?.current?.score ?? column.card?.score ?? null)
  rows.push({
    key: 'score',
    label: t('r_score'),
    compare: scores,
    direction: 'max',
    cells: scores.map((value, index) => (
      <span
        key={index}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 34,
          borderRadius: 8,
          padding: '3px 7px',
          fontSize: 17,
          fontWeight: 800,
          color: value === null ? 'var(--muted)' : scoreTone(value).fg,
          background: value === null ? 'transparent' : scoreTone(value).bg,
        }}
      >
        {value === null ? DASH : scoreText(value)}
      </span>
    )),
  })

  /* ── Asking price ──────────────────────────────────────────────────────── */
  const asking = cellOf(
    (column) =>
      (column.detail?.opportunity.asking_price_cents as number | null | undefined) ??
      column.card?.askingPriceCents ??
      null,
  )
  rows.push({
    key: 'asking',
    label: t('r_asking'),
    compare: asking,
    direction: 'min',
    cells: asking.map((value, index) => (
      <b key={index} style={{ fontSize: 15 }}>
        {money(value, locale)}
      </b>
    )),
  })

  /* ── Landed cost — the row this screen exists for ──────────────────────── */
  const landedExpected = cellOf(
    (column) => column.detail?.current?.landed_expected_cents ?? column.card?.landed?.expected ?? null,
  )
  rows.push({
    key: 'landed',
    label: t('r_landed'),
    compare: landedExpected,
    direction: 'min',
    cells: columns.map((column, index) => {
      const expected = landedExpected[index] ?? null
      const low = column.detail?.current?.landed_low_cents ?? column.card?.landed?.low ?? null
      const high = column.detail?.current?.landed_high_cents ?? column.card?.landed?.high ?? null
      return (
        <span key={index} style={{ display: 'block' }}>
          <b style={{ fontSize: 15, color: 'var(--money)' }}>{money(expected, locale)}</b>
          {low !== null && high !== null && (
            <span style={{ display: 'block', fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>
              {range({ low, expected: expected ?? low, high }, locale)}
            </span>
          )}
        </span>
      )
    }),
  })

  /* ── Condition ─────────────────────────────────────────────────────────── */
  const grades = cellOf((column) => (column.detail?.opportunity.condition as string | null) ?? null)
  rows.push({
    key: 'condition',
    label: t('r_condition'),
    compare: grades.map((grade) => {
      const at = (CONDITION_GRADES as readonly string[]).indexOf(grade ?? '')
      return at === -1 ? null : at
    }),
    direction: 'max',
    cells: grades.map((grade, index) => (
      <span key={index}>{grade ? conditionLabel(grade, locale) : t('unknown')}</span>
    )),
  })

  /* ── Location. No ordering: a place is not better than a place. ─────────── */
  rows.push({
    key: 'location',
    label: t('r_location'),
    compare: null,
    direction: 'min',
    cells: columns.map((column, index) => {
      const listing = column.detail?.listings[0]
      const where = [listing?.municipality ?? column.card?.municipality, listing?.island ?? column.card?.island]
        .filter(Boolean)
        .join(' · ')
      return <span key={index}>{where || listing?.location_text || DASH}</span>
    }),
  })

  /* ── Size. Also no ordering: bigger is a different decision, not a better
        one — it is exactly what the access rules turn into a blocker. ─────── */
  rows.push({
    key: 'size',
    label: t('r_size'),
    compare: null,
    direction: 'max',
    cells: columns.map((column, index) => {
      const opportunity = column.detail?.opportunity
      const length = (opportunity?.length_ft as number | null | undefined) ?? column.card?.lengthFt ?? null
      const width = (opportunity?.width_ft as number | null | undefined) ?? null
      return (
        <span key={index}>
          {length === null ? DASH : feet(length, locale)}
          {width !== null && (
            <span style={{ color: 'var(--muted)' }}> × {feet(width, locale)}</span>
          )}
        </span>
      )
    }),
  })

  /* ── Utilities ─────────────────────────────────────────────────────────── */
  const UTILITIES: { key: string; fact: string }[] = [
    { key: 'electric', fact: 'has_electric' },
    { key: 'plumbing', fact: 'has_plumbing' },
    { key: 'water', fact: 'has_water' },
    { key: 'septic', fact: 'has_septic' },
    { key: 'ac', fact: 'has_ac' },
  ]

  const utilities = columns.map((column) => {
    const facts = column.detail?.facts ?? []
    const present: string[] = []
    for (const utility of UTILITIES) {
      const fact = facts.find((one) => one.attribute === utility.fact)
      if (fact && truthy(fact.value)) present.push(utility.key)
    }
    if (truthy(column.detail?.opportunity.has_kitchen)) present.push('kitchen')
    if (truthy(column.detail?.opportunity.has_bathroom)) present.push('bathroom')
    return present
  })

  rows.push({
    key: 'utilities',
    label: t('r_utilities'),
    compare: utilities.map((present, index) => (columns[index]?.detail ? present.length : null)),
    direction: 'max',
    cells: utilities.map((present, index) => (
      <span key={index} style={{ display: 'block', lineHeight: 1.35 }}>
        {present.length === 0
          ? t('none')
          : present.map((one) => utilityLabel(one, locale)).join(', ')}
      </span>
    )),
  })

  /* ── Transport: the ACCESS roll-up, which is the answer that matters ────── */
  rows.push({
    key: 'transport',
    label: t('r_transport'),
    compare: null,
    direction: 'max',
    cells: columns.map((column, index) => {
      const access = column.transport?.access?.rollUp ?? null
      const plan = column.transport?.plan ?? null
      const quotes = column.transport?.quotes ?? []
      return (
        <span key={index} style={{ display: 'block', lineHeight: 1.35 }}>
          {access ? (
            <span
              style={{
                display: 'inline-block',
                borderRadius: 999,
                padding: '2px 7px',
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.03em',
                color: accessTone(access.level).fg,
                background: accessTone(access.level).bg,
              }}
            >
              {accessLabel(access.level, locale)}
            </span>
          ) : (
            <span style={{ color: 'var(--muted)' }}>{t('unknown')}</span>
          )}
          <span style={{ display: 'block', fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>
            {quotes.length === 0
              ? t('noQuotes')
              : plan
                ? planStatusLabel(plan.status, locale)
                : `${quotes.length}`}
          </span>
        </span>
      )
    }),
  })

  /* ── Repairs — the cost line, not a guess made here ─────────────────────── */
  const repairs = columns.map((column) => {
    const line = column.cost?.lines.find((one) => one.category === 'repairs')
    return line ? line.expected_cents : null
  })
  rows.push({
    key: 'repairs',
    label: t('r_repairs'),
    compare: repairs,
    direction: 'min',
    cells: repairs.map((value, index) => (
      <span key={index}>{value === null ? DASH : money(value, locale)}</span>
    )),
  })

  /* ── Risks ─────────────────────────────────────────────────────────────── */
  const risks = columns.map(
    (column) => (column.detail?.current?.risk_level as string | null) ?? column.card?.riskLevel ?? null,
  )
  rows.push({
    key: 'risks',
    label: t('r_risks'),
    compare: risks.map((level) => {
      const at = (RISK_LEVELS as readonly string[]).indexOf(level ?? '')
      return at === -1 ? null : at
    }),
    direction: 'min',
    cells: risks.map((level, index) => (
      <span key={index}>{level ? riskLevelLabel(level, locale) : t('unknown')}</span>
    )),
  })

  /* ── Seller status ─────────────────────────────────────────────────────── */
  rows.push({
    key: 'seller',
    label: t('r_seller'),
    compare: null,
    direction: 'max',
    cells: columns.map((column, index) => {
      const seller = column.seller
      if (!seller) {
        return (
          <span key={index} style={{ color: 'var(--muted)' }}>
            {column.detail?.opportunity.primary_contact_id ? t('loading') : t('noSeller')}
          </span>
        )
      }
      const days = seller.stale.daysSilent
      return (
        <span key={index} style={{ display: 'block', lineHeight: 1.35 }}>
          {known(seller.seller.status) ? humanise(seller.seller.status) : t('unknown')}
          <span style={{ display: 'block', fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>
            {seller.stale.awaitingReply
              ? t('awaiting')
              : days === null
                ? ''
                : t('silentDays').replace('{n}', String(days))}
          </span>
        </span>
      )
    }),
  })

  /* ── Stage, last: context rather than a comparison. ─────────────────────── */
  rows.push({
    key: 'stage',
    label: t('r_stage'),
    compare: null,
    direction: 'max',
    cells: columns.map((column, index) => {
      const stage = (column.detail?.opportunity.stage as string | undefined) ?? column.card?.stage ?? null
      return <span key={index}>{stage ? stageLabel(stage, locale) : DASH}</span>
    }),
  })

  return rows
}

/**
 * Which columns hold the winning value.
 *
 * `null` — nothing tinted — when the row has no ordering, when fewer than two
 * columns have a value, or when every value is the same. A tint that appears
 * on all four cells says "these differ" about four identical numbers.
 */
function winners(row: Row): Set<number> {
  if (!row.compare) return new Set()
  const values = row.compare
  const present = values.map((value, index) => ({ value, index })).filter((one) => one.value !== null) as {
    value: number
    index: number
  }[]
  if (present.length < 2) return new Set()
  const first = present[0]
  if (!first) return new Set()
  if (present.every((one) => one.value === first.value)) return new Set()

  const best = present.reduce(
    (held, one) => (row.direction === 'min' ? Math.min(held, one.value) : Math.max(held, one.value)),
    first.value,
  )
  return new Set(present.filter((one) => one.value === best).map((one) => one.index))
}

/* ────────────────────────────────────────────────────────────────────────────
 * The grid
 * ────────────────────────────────────────────────────────────────────────── */

function Grid({
  columns,
  rows,
  t,
  onRemove,
}: {
  columns: ColumnData[]
  rows: Row[]
  t: (key: CopyKey) => string
  onRemove: (id: string) => void
}) {
  const template = `${LABEL_WIDTH}px repeat(${columns.length}, ${COLUMN_WIDTH}px)`

  const labelCell: CSSProperties = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: 'var(--bg)',
    borderRight: '1px solid var(--line)',
    padding: '9px 8px',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    display: 'flex',
    alignItems: 'center',
  }

  return (
    <div
      style={{
        overflowX: 'auto',
        scrollSnapType: 'x proximity',
        border: '1px solid var(--line)',
        borderRadius: 12,
        background: 'var(--surface)',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: template, minWidth: 'min-content' }}>
        {/* Header */}
        <div style={{ ...labelCell, alignItems: 'flex-end', paddingBottom: 10 }} />
        {columns.map((column) => {
          const title = column.detail?.opportunity.title ?? column.card?.title ?? column.id
          const ref = column.detail?.opportunity.ref ?? column.card?.ref ?? null
          return (
            <div
              key={column.id}
              style={{
                scrollSnapAlign: 'start',
                padding: '9px 8px 10px',
                borderLeft: '1px solid var(--line)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)' }}>{ref ?? ''}</span>
                <button
                  type="button"
                  onClick={() => onRemove(column.id)}
                  aria-label={t('remove')}
                  title={t('remove')}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    color: 'var(--muted)',
                    cursor: 'pointer',
                    lineHeight: 1,
                  }}
                >
                  <Icon name="close" size={13} />
                </button>
              </span>
              <Link
                to={`/o/${encodeURIComponent(column.id)}`}
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  color: 'var(--ink)',
                  textDecoration: 'none',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {String(title)}
              </Link>
              {column.failed && (
                <span style={{ fontSize: 10.5, color: 'var(--danger)' }}>{t('failed')}</span>
              )}
              {!column.detail && !column.failed && (
                <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>{t('loading')}</span>
              )}
            </div>
          )
        })}

        {/* Rows */}
        {rows.map((row) => {
          const best = winners(row)
          return (
            <ComparisonRow key={row.key} row={row} best={best} labelCell={labelCell} tintTitle={t('best')} />
          )
        })}
      </div>
    </div>
  )
}

function ComparisonRow({
  row,
  best,
  labelCell,
  tintTitle,
}: {
  row: Row
  best: Set<number>
  labelCell: CSSProperties
  tintTitle: string
}) {
  return (
    <>
      <div style={{ ...labelCell, borderTop: '1px solid var(--line)' }}>{row.label}</div>
      {row.cells.map((cell, index) => {
        const won = best.has(index)
        return (
          <div
            key={index}
            title={won ? tintTitle : undefined}
            style={{
              scrollSnapAlign: 'start',
              borderTop: '1px solid var(--line)',
              borderLeft: '1px solid var(--line)',
              padding: '9px 8px',
              fontSize: 12.5,
              /* The tint is a background, not a colour: a green number is a
                 second meaning for a colour the score badges already own. */
              background: won ? TONES.strong.bg : 'transparent',
              fontWeight: won ? 800 : 500,
            }}
          >
            {cell}
          </div>
        )
      })}
    </>
  )
}
